import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { createQrList, downloadQrList } from '../../services/qrService';
import { SuccessCircle, WarningIcon, IosArrowLeft, IosArrowRight } from '../../assets/icons';
import useModalClose from '../../hooks/useModalClose';
import Spinner from '../common/Spinner';
import { formatDateOption, getTodayAndTomorrow } from '../../helpers/stringHelpers';

const STEPS = {
  FORM: 0,
  CONFIRM: 1,
  COMPLETE: 2
};

const INITIAL_FORM_STATE = {
  count: '',
  date: new Date().toISOString().split('T')[0]
};

const CreateQrModal = React.memo(({ isOpen, onClose, onSuccess }) => {
  // Use useMemo to maintain the initial state across re-renders
  const initialState = useMemo(() => ({
    currentStep: STEPS.FORM,
    formData: INITIAL_FORM_STATE,
    error: null,
    isSubmitting: false,
    createdQr: null,
    abortController: null
  }), []);

  const [state, setState] = useState(initialState);
  useModalClose(isOpen, onClose);

  const updateState = (updates) => {
    setState(current => ({
      ...current,
      ...updates
    }));
  };

  // Reset state ONLY when modal is explicitly closed
  useEffect(() => {
    if (!isOpen) {
      const timeout = setTimeout(() => {
        setState(initialState);
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, [isOpen, initialState]);

  const handleClose = () => {
    onClose();
  };

  const handleNewQrCode = () => {
    setState(initialState);
  };

  const handleConfirm = async () => {
    updateState({ isSubmitting: true, error: null });

    const controller = new AbortController();
    updateState({ abortController: controller });

    try {
      const result = await createQrList(state.formData, controller.signal);
      if (result.status === 200) {
        // First update our local state to show success
        updateState({
          createdQr: result,
          currentStep: STEPS.COMPLETE
        });

        // Start the download
        await handleDownload(false);

        // Only after showing success and starting download, notify parent
        const qrData = {
          id: result.data.id,
          count: parseInt(state.formData.count),
          olusturulmaTarihi: state.formData.date,
          indirildiMi: false
        };
        setTimeout(() => onSuccess(qrData), 100);
      } else {
        throw new Error('QR kod oluşturma işlemi başarısız oldu.');
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        updateState({ error: 'İşlem iptal edildi.' });
      } else {
        console.error('Failed to create QR codes:', error);
        updateState({ 
          error: 'QR kod oluşturulurken bir hata oluştu. Lütfen tekrar deneyiniz.',
          currentStep: STEPS.FORM 
        });
      }
    } finally {
      updateState({ 
        isSubmitting: false,
        abortController: null
      });
    }
  };

  const handleCancelRequest = () => {
    if (state.abortController) {
      state.abortController.abort();
    }
    handleClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'count') {
      const numValue = parseInt(value, 10);
      if (numValue > 1500) {
        updateState({ error: 'Maksimum 1500 adet QR kod oluşturabilirsiniz.' });
        return;
      }
      if (numValue < 1) {
        updateState({ error: 'En az 1 adet QR kod oluşturmalısınız.' });
        return;
      }
      updateState({ error: null });
    }

    updateState({
      formData: {
        ...state.formData,
        [name]: value
      }
    });
  };

  const validateForm = () => {
    const count = parseInt(state.formData.count, 10);
    if (!count || count < 1) {
      updateState({ error: 'Lütfen geçerli bir QR kod adedi giriniz.' });
      return false;
    }
    if (count > 1500) {
      updateState({ error: 'Maksimum 1500 adet QR kod oluşturabilirsiniz.' });
      return false;
    }
    if (!state.formData.date) {
      updateState({ error: 'Lütfen bir tarih seçiniz.' });
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateState({ error: null });
    
    if (validateForm()) {
      updateState({ currentStep: STEPS.CONFIRM });
    }
  };

  const handleBack = () => {
    updateState({ currentStep: STEPS.FORM });
  };

  const handleDownload = async (showError = true) => {
    try {
      if (!state.createdQr || !state.createdQr.id) {
        if (showError) {
          updateState({ error: 'QR kod ID\'si bulunamadı.' });
        }
        return;
      }
      await downloadQrList(state.createdQr.id);
    } catch (error) {
      console.error('Failed to download QR:', error);
      if (showError) {
        updateState({ error: 'QR kodlar indirilirken bir hata oluştu. Lütfen tekrar deneyiniz.' });
      }
    }
  };

  const renderStepIndicator = () => (
    <div className="stepper">
      <div className={`step ${state.currentStep >= STEPS.FORM ? 'active' : ''}`}>
        <div className="step-circle">
          {state.currentStep >= STEPS.FORM && <div className='step-active-circle' />}
        </div>
        <span className="step-title">QR Bilgileri</span>
      </div>
      <div className={`step ${state.currentStep >= STEPS.CONFIRM ? 'active' : ''}`}>
        <div className="step-circle">
          {state.currentStep >= STEPS.CONFIRM && <div className='step-active-circle' />}
        </div>
        <span className="step-title">Bilgi Onayı</span>
      </div>
      <div className={`step ${state.currentStep >= STEPS.COMPLETE ? 'active' : ''}`}>
        <div className="step-circle">
          {state.currentStep >= STEPS.COMPLETE && <div className='step-active-circle' />}
        </div>
        <span className="step-title">Tamamlandı</span>
      </div>
    </div>
  );

  const renderForm = () => {
    const dates = getTodayAndTomorrow();

    return (
      <>
        <div className='create-qr-helper-text'>
            <p>Maksimum <span>1.500 adet QR kodu, B1 sayfa (100 cm x 70 cm)</span> boyutunda ve toplam <span>10 sayfa</span> olacak şekilde basılabilmektedir.</p>
        </div>
        <form onSubmit={handleSubmit}>
            <div className="form-group">
                <label htmlFor="date">Tarih</label>
                <select
                    id="date"
                    name="date"
                    value={state.formData.date}
                    onChange={handleChange}
                    required
                    disabled={state.isSubmitting}
                    className="date-select"
                >
                    <option value={dates.today}>
                        {formatDateOption(dates.today, 'Bugün')}
                    </option>
                    <option value={dates.tomorrow}>
                        {formatDateOption(dates.tomorrow, 'Yarın')}
                    </option>
                </select>
            </div>
            <div className="form-group">
                <label htmlFor="count">QR Kod Adedi</label>
                <input
                    type="tel"
                    id="count"
                    name="count"
                    value={state.formData.count}
                    onChange={handleChange}
                    min="1"
                    max="1500"
                    required
                    disabled={state.isSubmitting}
                />
            </div>

            {state.error && <div className="error-message">{state.error}</div>}

            <div className="form-actions">
                <button 
                    className="first-step-cancel-button"
                    type="button"
                    onClick={handleClose}
                >
                    <IosArrowLeft color='#C70000' /> İptal Et
                </button>
                <button 
                    type="submit" 
                    className="first-step-submit-button"
                    disabled={state.isSubmitting || !!state.error}
                >
                    Devam Et <IosArrowRight color='#18964E' />
                </button>
            </div>
        </form>
      </>
    );
  };

  const renderConfirmation = () => (
    <div className="confirmation-step">
      {state.isSubmitting ? (
        <div className="loading-state">
          <Spinner size='large' />
          <h3>Süreciniz tamamlanıyor...</h3>
          <p>Bu işlem bir dakikadan fazla sürebilir.</p>
          <div className="form-actions">
            <button 
              type="button" 
              className="loading-close-button"
              onClick={handleCancelRequest}
            >
              Süreci İptal Et
            </button>
          </div>
        </div>
      ) : (
        <>
          <WarningIcon />
          <h3>Sistem üzerinden {state.formData.count} tane QR Kod basılacaktır. <br/> Onaylıyor musunuz? </h3>
          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-button"
              onClick={handleClose}
              disabled={state.isSubmitting}
            >
              <IosArrowLeft color='#C70000' /> İptal Et
            </button>
            <div className='go-back-and-next-buttons'>
                <button 
                    type="button" 
                    className="go-back-button"
                    onClick={handleBack}
                    disabled={state.isSubmitting}
                >
                    <IosArrowLeft color='#2A353D' />Önceki
                </button>
                <button 
                type="button" 
                className="submit-button"
                onClick={handleConfirm}
                disabled={state.isSubmitting}
                >
                    Sonraki <IosArrowRight color='#18964E' />
                </button>
            </div>
          </div>
        </>
      )}
      {state.error && <div className="error-message">{state.error}</div>}
    </div>
  );

  const renderComplete = () => (
    <div className="complete-step">
      <SuccessCircle />
      <h3>Süreciniz tamamlandı. Dosyanız iniyor.</h3>
      <p>
        İnme süreci başlamadıysa{' '}
        <button 
          onClick={() => handleDownload(true)}
          className="download-link"
        >
          buraya
        </button>
        {' '}tıklayarak tekrar deneyebilirsiniz.
        <br/> 
        {state.formData.count} QR kod dosyanız hazırlanmıştır.
      </p>
      <div className="form-actions">
        <button 
          type="button" 
          className="complete-step-button"
          onClick={handleNewQrCode}
        >
          Yeniden QR Kodu Oluştur
        </button>
      </div>
      {state.error && <div className="error-message">{state.error}</div>}
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content create-qr-modal-content">
        <div className="modal-header">
          <h2>Yeni QR Kod Oluştur</h2>
        </div>

        {renderStepIndicator()}

        <div className="modal-body">
          {state.currentStep === STEPS.FORM && renderForm()}
          {state.currentStep === STEPS.CONFIRM && renderConfirmation()}
          {state.currentStep === STEPS.COMPLETE && renderComplete()}
        </div>
      </div>
    </div>
  );
});

CreateQrModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired
};

export default CreateQrModal; 